import { drag } from 'd3-drag';
import { pointer, select, type Selection } from 'd3-selection';
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';

export type JourneyCategory = 'work' | 'relation' | 'project';
export type JourneyState = 'alive' | 'dead' | 'ambiguous' | 'red_zone' | 'unknown';

export type GraphNode =
  | { id: string; type: 'journey'; label: string; depth: 0 }
  | { id: string; type: 'element'; elementId: string; label: string; category: JourneyCategory; state: JourneyState; depth: 1 }
  | { id: string; type: 'observation'; observationId: string; label: string; elementId: string; depth: 2 }
  | { id: string; type: 'mechanism'; mechanismId: string; label: string; status: 'confirmed' | 'suggested'; depth: 3; suggestionId?: string };

export interface GraphLink {
  id: string;
  source: string | LayoutNode;
  target: string | LayoutNode;
  type: 'hierarchy' | 'mechanism_confirmed' | 'mechanism_suggested';
  confidence?: 'low' | 'medium' | 'high';
  description?: string;
  analysisId?: string;
}

const GRAPH_COLORS = {
  accent: '#6366f1',
  bgCard: '#16161f',
  border: '#252538',
  mechanism: '#7c3aed',
  text: '#e2e8f0',
  category: {
    work: '#6366f1',
    relation: '#ec4899',
    project: '#06b6d4',
  } satisfies Record<JourneyCategory, string>,
  observationState: {
    alive: '#10b981',
    ambiguous: '#f59e0b',
    red_zone: '#ef4444',
    dead: '#6b7280',
    unknown: '#6b7280',
  } satisfies Record<JourneyState, string>,
};

const MIN_VIEW_WIDTH = 960;
const MIN_VIEW_HEIGHT = 640;
const LAYER_LABELS = ['Карта', 'Сферы', 'Наблюдения', 'Механизмы'];

type LayoutNode = GraphNode & { x: number; y: number };

export class D3JourneyGraph {
  private containerId: string;
  private svgId: string;
  private journeyId: string | null = null;

  private svg: Selection<SVGSVGElement, unknown, null, undefined> | null = null;
  private gRoot: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private gGuides: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private gLinks: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private gNodes: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private zoomBehavior: ZoomBehavior<SVGSVGElement, unknown> | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private onNodeClick: (node: GraphNode) => void;
  private onSuggestionResolved: () => void;

  private nodes: LayoutNode[] = [];
  private links: GraphLink[] = [];
  private layerY: number[] = [80, 210, 380, 540];
  private viewWidth = MIN_VIEW_WIDTH;
  private viewHeight = MIN_VIEW_HEIGHT;

  private hoveredNodeId: string | null = null;

  constructor(
    containerId: string,
    svgId: string,
    onNodeClick: (node: GraphNode) => void,
    onSuggestionResolved: () => void
  ) {
    this.containerId = containerId;
    this.svgId = svgId;
    this.onNodeClick = onNodeClick;
    this.onSuggestionResolved = onSuggestionResolved;
  }

  async load(journeyId: string) {
    this.journeyId = journeyId;
    this.showState('loading');

    try {
      const token = localStorage.getItem('avgexpert_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(
        `/api/journeys/${journeyId}/graph?include=suggestions&hideArchived=true`,
        { headers }
      );
      if (!response.ok) throw new Error('Ошибка сети при загрузке графа');
      const data = await response.json();

      this.nodes = (data.nodes || []).map((n: GraphNode) => ({ ...n, x: 0, y: 0 }));
      this.links = (data.links || []).map((l: GraphLink) => ({ ...l }));

      if (this.nodes.length === 0) {
        this.showState('empty');
        return;
      }

      this.showState('graph');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.initGraph());
      });
    } catch (err) {
      console.error(err);
      this.showState('error');
    }
  }

  private showState(state: 'loading' | 'empty' | 'error' | 'graph') {
    const loading = document.getElementById('mycelium-loading');
    const empty = document.getElementById('mycelium-empty');
    const error = document.getElementById('mycelium-error');
    const svg = document.getElementById(this.svgId);

    const hideOverlay = (el: HTMLElement | null) => {
      if (!el) return;
      el.style.display = 'none';
      el.classList.add('hidden');
    };

    hideOverlay(loading);
    hideOverlay(empty);
    hideOverlay(error);

    if (state === 'loading' && loading) {
      loading.style.display = 'flex';
      loading.classList.remove('hidden');
    } else if (state === 'empty' && empty) {
      empty.style.display = 'flex';
      empty.classList.remove('hidden');
    } else if (state === 'error' && error) {
      error.style.display = 'flex';
      error.classList.remove('hidden');
    }

    if (svg) {
      const showGraph = state === 'graph';
      svg.style.display = showGraph ? 'block' : 'none';
      svg.classList.toggle('hidden', !showGraph);
      svg.style.pointerEvents = showGraph ? 'auto' : 'none';
    }
  }

  private initGraph() {
    const containerEl = document.getElementById(this.containerId);
    const svgEl = document.getElementById(this.svgId) as SVGSVGElement | null;
    if (!containerEl || !svgEl) return;

    this.destroy();

    this.applyLayeredLayout();
    this.resolveLinkEndpoints();

    this.svg = select(svgEl);
    this.svg.selectAll('*').remove();
    this.svg
      .attr('viewBox', `0 0 ${this.viewWidth} ${this.viewHeight}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    this.gRoot = this.svg.append('g');
    this.gGuides = this.gRoot.append('g').attr('class', 'layer-guides');
    this.gLinks = this.gRoot.append('g').attr('class', 'links');
    this.gNodes = this.gRoot.append('g').attr('class', 'nodes');

    this.renderGuides();
    this.renderLinks();
    this.renderNodes();

    this.zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.35, 4])
      .filter((event) => {
        if (event.type === 'wheel' || event.type === 'dblclick') return true;
        const target = event.target as Element | null;
        return !target?.closest('g.node');
      })
      .on('zoom', (event) => {
        this.gRoot?.attr('transform', event.transform);
      });
    this.svg.call(this.zoomBehavior);
    this.svg.on('dblclick.zoom', null);
    this.svg.on('dblclick', (event) => {
      event.preventDefault();
      this.resetView();
    });

    this.showState('graph');

    const centerBtn = document.getElementById('mycelium-center-btn');
    if (centerBtn) {
      centerBtn.onclick = () => {
        this.applyLayeredLayout();
        this.resolveLinkEndpoints();
        this.renderGuides();
        this.renderLinks();
        this.renderNodes();
        this.resetView();
      };
    }

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(containerEl);
  }

  private handleResize() {
    if (!this.svg) return;
    this.svg
      .attr('viewBox', `0 0 ${this.viewWidth} ${this.viewHeight}`)
      .attr('width', '100%')
      .attr('height', '100%');
  }

  private resetView() {
    if (!this.svg || !this.zoomBehavior) return;
    this.svg.call(this.zoomBehavior.transform, zoomIdentity);
  }

  private layerYFor(depth: number): number {
    const d = Number(depth);
    return this.layerY[Number.isFinite(d) ? d : 3] ?? this.layerY[3];
  }

  private applyLayeredLayout() {
    const layers = new Map<number, LayoutNode[]>();
    for (const node of this.nodes) {
      const depth = Number(node.depth ?? 3);
      if (!layers.has(depth)) layers.set(depth, []);
      layers.get(depth)!.push(node);
    }

    let maxInLayer = 1;
    for (const layerNodes of layers.values()) {
      maxInLayer = Math.max(maxInLayer, layerNodes.length);
    }

    const nodeGap = maxInLayer > 8 ? 88 : maxInLayer > 5 ? 76 : 64;
    const margin = 80;
    this.viewWidth = Math.max(MIN_VIEW_WIDTH, margin * 2 + maxInLayer * nodeGap);
    this.viewHeight = Math.max(MIN_VIEW_HEIGHT, 80 + layers.size * 150);

    const layerStep = Math.max(130, Math.floor((this.viewHeight - 120) / Math.max(layers.size - 1, 1)));
    this.layerY = Array.from({ length: 4 }, (_, i) => 70 + i * layerStep);

    const left = margin;
    const right = this.viewWidth - margin;
    const span = right - left;

    for (const [depth, layerNodes] of layers) {
      const y = this.layerYFor(depth);
      const count = layerNodes.length;
      layerNodes.forEach((node, index) => {
        node.x = left + ((index + 1) / (count + 1)) * span;
        node.y = y;
      });
    }
  }

  private resolveLinkEndpoints() {
    const nodeById = new Map(this.nodes.map((n) => [n.id, n]));
    for (const link of this.links) {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      const source = nodeById.get(sourceId);
      const target = nodeById.get(targetId);
      if (source) link.source = source;
      if (target) link.target = target;
    }
  }

  private renderGuides() {
    if (!this.gGuides) return;
    const guides = this.gGuides.selectAll<SVGLineElement, number>('line.layer-guide')
      .data(this.layerY, (d) => String(d));

    guides.join('line')
      .attr('class', 'layer-guide')
      .attr('x1', 40)
      .attr('x2', this.viewWidth - 40)
      .attr('y1', (d) => d)
      .attr('y2', (d) => d)
      .attr('stroke', GRAPH_COLORS.border)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 6')
      .attr('opacity', 0.45);

    const labels = this.gGuides.selectAll<SVGTextElement, { y: number; label: string }>('text.layer-label')
      .data(
        this.layerY.map((y, i) => ({ y, label: LAYER_LABELS[i] || '' })),
        (d) => d.label
      );

    labels.join('text')
      .attr('class', 'layer-label')
      .attr('x', 40)
      .attr('y', (d) => d.y - 8)
      .attr('fill', '#64748b')
      .attr('font-size', 11)
      .text((d) => d.label);
  }

  private renderLinks() {
    if (!this.gLinks) return;

    this.gLinks.selectAll<SVGLineElement, GraphLink>('line.link')
      .data(this.links, (d) => d.id)
      .join('line')
      .attr('class', (d) => `link link-${d.type}`)
      .attr('x1', (d) => (typeof d.source === 'object' ? d.source.x : 0))
      .attr('y1', (d) => (typeof d.source === 'object' ? d.source.y : 0))
      .attr('x2', (d) => (typeof d.target === 'object' ? d.target.x : 0))
      .attr('y2', (d) => (typeof d.target === 'object' ? d.target.y : 0))
      .attr('stroke', (d) => (d.type === 'hierarchy' ? GRAPH_COLORS.border : GRAPH_COLORS.mechanism))
      .attr('stroke-width', (d) => (d.type === 'hierarchy' ? 1.5 : 2))
      .attr('stroke-dasharray', (d) => (d.type === 'mechanism_suggested' ? '4 4' : null))
      .attr('stroke-opacity', (d) => {
        if (d.type === 'hierarchy') return 0.35;
        if (d.type === 'mechanism_suggested') return 0.45;
        return 0.75;
      });
  }

  private updateLinkPositions() {
    if (!this.gLinks) return;
    this.gLinks.selectAll<SVGLineElement, GraphLink>('line.link')
      .attr('x1', (d) => (typeof d.source === 'object' ? d.source.x : 0))
      .attr('y1', (d) => (typeof d.source === 'object' ? d.source.y : 0))
      .attr('x2', (d) => (typeof d.target === 'object' ? d.target.x : 0))
      .attr('y2', (d) => (typeof d.target === 'object' ? d.target.y : 0));
  }

  private renderNodes() {
    if (!this.gNodes) return;

    const gRootNode = this.gRoot?.node();
    if (!gRootNode) return;

    const dragBehavior = drag<SVGGElement, LayoutNode>()
      .clickDistance(4)
      .on('start', (event) => {
        event.sourceEvent.stopPropagation();
        select(event.sourceEvent.currentTarget as SVGGElement).raise().classed('dragging', true);
      })
      .on('drag', (event, d) => {
        const [x, y] = pointer(event, gRootNode);
        d.x = x;
        d.y = y;
        select(event.sourceEvent.currentTarget as SVGGElement)
          .attr('transform', `translate(${x},${y})`);
        this.updateLinkPositions();
        this.hideTooltip();
      })
      .on('end', (event) => {
        select(event.sourceEvent.currentTarget as SVGGElement).classed('dragging', false);
      });

    const nodeGroups = this.gNodes.selectAll<SVGGElement, LayoutNode>('g.node')
      .data(this.nodes, (d) => d.id)
      .join('g')
      .attr('class', (d) => `node node-${d.type}`)
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .call(dragBehavior);

    nodeGroups.selectAll('circle').remove();
    nodeGroups.selectAll('text.node-label').remove();

    nodeGroups.append('circle')
      .attr('r', (d) => this.nodeRadius(d))
      .attr('fill', (d) => this.nodeFill(d))
      .attr('stroke', GRAPH_COLORS.bgCard)
      .attr('stroke-width', 1.5);

    nodeGroups.filter((d) => d.type === 'mechanism' && d.status === 'suggested')
      .select('circle')
      .attr('fill', 'transparent')
      .attr('stroke', GRAPH_COLORS.mechanism)
      .attr('stroke-dasharray', '3 3');

    nodeGroups.append('text')
      .attr('class', 'node-label')
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => this.nodeRadius(d) + 14)
      .attr('fill', GRAPH_COLORS.text)
      .attr('font-size', (d) => (d.type === 'journey' ? 12 : 10))
      .attr('opacity', 0)
      .text((d) => this.truncate(d.label, d.type === 'observation' ? 24 : 36));

    nodeGroups
      .on('click', (event, d) => {
        event.stopPropagation();
        this.onNodeClick(d);
      })
      .on('mouseenter', (event, d) => {
        this.hoveredNodeId = d.id;
        this.highlightNodeRelations(d);
        select(event.currentTarget).select('text.node-label').attr('opacity', 1);
        this.showTooltip(event, d);
      })
      .on('mouseleave', (event) => {
        this.hoveredNodeId = null;
        this.clearHighlight();
        select(event.currentTarget).select('text.node-label').attr('opacity', 0);
        this.hideTooltip();
      });
  }

  private nodeRadius(d: LayoutNode): number {
    if (d.type === 'journey') return 18;
    if (d.type === 'element') return 12;
    if (d.type === 'observation') return 8;
    return 6;
  }

  private nodeFill(d: LayoutNode): string {
    if (d.type === 'journey') return GRAPH_COLORS.accent;
    if (d.type === 'element') {
      return GRAPH_COLORS.category[d.category as JourneyCategory] || GRAPH_COLORS.accent;
    }
    if (d.type === 'observation') {
      const state = (d as LayoutNode & { state?: JourneyState }).state;
      return GRAPH_COLORS.observationState[state || 'unknown'] || GRAPH_COLORS.observationState.unknown;
    }
    if (d.status === 'suggested') return 'transparent';
    return GRAPH_COLORS.mechanism;
  }

  private highlightNodeRelations(node: LayoutNode) {
    if (!this.gNodes || !this.gLinks) return;

    const relatedIds = new Set<string>([node.id]);
    const relatedLinkIds = new Set<string>();

    for (const l of this.links) {
      const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
      const targetId = typeof l.target === 'object' ? l.target.id : l.target;
      if (sourceId === node.id || targetId === node.id) {
        relatedIds.add(sourceId);
        relatedIds.add(targetId);
        relatedLinkIds.add(l.id);
      }
    }

    this.gNodes.selectAll<SVGGElement, LayoutNode>('g.node')
      .classed('dimmed', (d) => !relatedIds.has(d.id))
      .classed('highlighted', (d) => relatedIds.has(d.id));

    this.gLinks.selectAll<SVGLineElement, GraphLink>('line.link')
      .classed('dimmed', (d) => !relatedLinkIds.has(d.id))
      .classed('highlighted', (d) => relatedLinkIds.has(d.id));
  }

  private clearHighlight() {
    this.gNodes?.selectAll('g.node').classed('dimmed highlighted', false);
    this.gLinks?.selectAll('line.link').classed('dimmed highlighted', false);
  }

  getLinkedObservationId(node: GraphNode): string | null {
    if (node.type === 'observation') return node.observationId;

    if (node.type === 'mechanism') {
      for (const link of this.links) {
        if (link.type !== 'mechanism_confirmed' && link.type !== 'mechanism_suggested') continue;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        if (targetId !== node.id) continue;
        const source = typeof link.source === 'object' ? link.source : this.nodes.find((n) => n.id === link.source);
        if (source?.type === 'observation') return source.observationId;
      }
    }

    return null;
  }

  private showTooltip(event: MouseEvent, node: LayoutNode) {
    const tooltip = document.getElementById('graph-tooltip');
    const container = document.getElementById(this.containerId);
    if (!tooltip || !container || !this.svg) return;

    let html = '';
    if (node.type === 'journey') html = `<strong>Карта:</strong> ${node.label}`;
    else if (node.type === 'element') html = `<strong>Сфера:</strong> ${node.label}`;
    else if (node.type === 'observation') html = `<strong>Наблюдение:</strong> ${node.label}`;
    else if (node.type === 'mechanism') {
      const hasRag = this.links.some(
        (l) =>
          (l.type === 'mechanism_suggested' || l.type === 'mechanism_confirmed') &&
          (typeof l.target === 'object' ? l.target.id : l.target) === node.id &&
          !!l.analysisId
      );
      html = `<strong>Механизм:</strong> ${node.label}<br>${node.status === 'suggested' ? 'Гипотеза ИИ — клик для просмотра' : 'Подтверждено — клик для связанного наблюдения'}`;
      if (hasRag) html += '<br><span style="color:#a78bfa;font-size:0.8rem;">📚 RAG-анализ</span>';
    }

    const rect = container.getBoundingClientRect();
    const [mx, my] = pointer(event, this.svg.node() as SVGSVGElement);
    tooltip.style.display = 'block';
    tooltip.innerHTML = html;
    tooltip.style.left = `${Math.min(mx + 12, rect.width - 280)}px`;
    tooltip.style.top = `${Math.min(my + 16, rect.height - 80)}px`;
  }

  private hideTooltip() {
    const tooltip = document.getElementById('graph-tooltip');
    if (tooltip) tooltip.style.display = 'none';
  }

  pause() {}
  resume() {}

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.svg) {
      this.svg.on('.zoom', null);
      this.svg.on('dblclick', null);
      this.svg.selectAll('*').remove();
      this.svg = null;
    }
    this.gRoot = null;
    this.gGuides = null;
    this.gLinks = null;
    this.gNodes = null;
    this.zoomBehavior = null;
    this.hideTooltip();
  }

  fitToScreen() {
    this.applyLayeredLayout();
    this.resolveLinkEndpoints();
    this.renderGuides();
    this.renderLinks();
    this.renderNodes();
    this.resetView();
  }

  private truncate(str: string, maxLen: number): string {
    return str.length <= maxLen ? str : `${str.slice(0, maxLen)}…`;
  }
}
